import { inheritsLoose as _inheritsLoose, typeof as _typeof } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import React, { Component } from 'react';
import filter from 'lodash/filter';
import _isEmpty from 'lodash/isEmpty';
import minBy from 'lodash/minBy';
import PropTypes from 'prop-types';
import { withTheme } from 'styled-components';
import { line } from 'd3-shape';
import { extent } from 'd3-array';
import { scaleOrdinal, schemeCategory20c, scaleTime, scaleLinear } from 'd3-scale';
import MousePosition from '../Chart/common/MousePosition.js';
import Chart from '../Chart/Chart.js';
import XAxis from '../Chart/XAxis.js';
import YAxis from '../Chart/YAxis.js';
import Line from './Line.js';
import DataSeries from './DataSeries.js';
import Legend from './Legend.js';
import { ChartInfoPoints, ChartInfoPointsLegend } from './ChartInfoPoints.js';
import ChartDescription from './ChartDescription.js';
import { handleFieldChangeEvent } from '../../AmortizationCalculator/AmortizationCalculator.Clickstream.js';

var _class;
function flattenData(data, xAccessor, yAccessor) {
  var allValues = [];
  var xValues = [];
  var yValues = [];
  var coincidentCoordinateCheck = {};
  data.forEach(function (series, i) {
    series.values.forEach(function (item, j) {
      var x = xAccessor(item);

      // Check for NaN since d3's Voronoi cannot handle NaN values
      // Go ahead and Proceed to next iteration since we don't want NaN
      // in allValues or in xValues or yValues
      if (isNaN(x)) {
        return;
      }
      xValues.push(x);
      var y = yAccessor(item);
      // when yAccessor returns an object (as in the case of candlestick)
      // iterate over the keys and push all the values to yValues array
      var yNode;
      if (_typeof(y) === 'object' && Object.keys(y).length > 0) {
        Object.keys(y).forEach(function (key) {
          // Check for NaN since d3's Voronoi cannot handle NaN values
          // Go ahead and Proceed to next iteration since we don't want NaN
          // in allValues or in xValues or yValues
          if (isNaN(y[key])) {
            return;
          }
          yValues.push(y[key]);
          // if multiple y points are to be plotted for a single x
          // as in the case of candlestick, default to y value of 0
          yNode = 0;
        });
      } else {
        // Check for NaN since d3's Voronoi cannot handle NaN values
        // Go ahead and Proceed to next iteration since we don't want NaN
        // in allValues or in xValues or yValues
        if (isNaN(y)) {
          return;
        }
        yValues.push(y);
        yNode = y;
      }
      var xyCoords = x + "-" + yNode;
      if (coincidentCoordinateCheck.hasOwnProperty(xyCoords)) {
        // Proceed to next iteration if the x y pair already exists
        // d3's Voronoi cannot handle NaN values or coincident coords
        // But we push them into xValues and yValues above because
        // we still may handle them there (labels, etc.)
        return;
      }
      coincidentCoordinateCheck[xyCoords] = '';
      var pointItem = {
        coord: {
          x: x,
          y: yNode
        },
        d: item,
        id: series.name + j,
        series: series,
        seriesIndex: i
      };
      allValues.push(pointItem);
    });
  });
  return {
    allValues: allValues,
    xValues: xValues,
    yValues: yValues
  };
}
function calculateScales(chartWidth, chartHeight, xValues, yValues) {
  var xScale, yScale;
  if (xValues.length > 0 && Object.prototype.toString.call(xValues[0]) === '[object Date]') {
    xScale = scaleTime().range([0, chartWidth]);
  } else {
    xScale = scaleLinear().range([0, chartWidth]);
  }
  xScale.domain(extent(xValues));
  if (yValues.length > 0 && Object.prototype.toString.call(yValues[0]) === '[object Date]') {
    yScale = scaleTime().range([chartHeight, 0]);
  } else {
    yScale = scaleLinear().range([chartHeight, 0]);
  }
  yScale.domain(extent(yValues));
  return {
    xScale: xScale,
    yScale: yScale
  };
}
var LineChart = withTheme((_class = /*#__PURE__*/function (_Component) {
  _inheritsLoose(LineChart, _Component);
  function LineChart() {
    var _this;
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    _this = _Component.call.apply(_Component, [this].concat(args)) || this;
    _this._nearest = function (data, val, xA) {
      if (data && data.length) {
        var i = 0,
          diff = Math.abs(val - xA(data[i])),
          iDiff;
        for (i = 1; i < data.length; i += 1) {
          iDiff = Math.abs(val - xA(data[i]));
          if (iDiff > diff) {
            break;
          }
          diff = iDiff;
        }
        return i - 1;
      }
      return null;
    };
    return _this;
  }
  var _proto = LineChart.prototype;
  _proto.render = function render() {
    var _this2 = this,
      _dataArray$,
      _dataArray$2,
      _dataArray$0$values,
      _dataArray$3;
    var _this$props = this.props,
      data = _this$props.data,
      _this$props$interpola = _this$props.interpolationType,
      interpolationType = _this$props$interpola === void 0 ? this.interpolate ? 'cardinal' : 'linear' : _this$props$interpola,
      containerWidth = _this$props.containerWidth,
      margins = _this$props.margins,
      height = _this$props.height,
      legend = _this$props.legend,
      legendOffset = _this$props.legendOffset,
      xAccessor = _this$props.xAccessor,
      yAccessor = _this$props.yAccessor,
      mouseX = _this$props.mouseX,
      mouseY = _this$props.mouseY,
      colors = _this$props.colors,
      lineWidth = _this$props.lineWidth,
      title = _this$props.title,
      xAxisTickCount = _this$props.xAxisTickCount,
      pointRadius = _this$props.pointRadius,
      displayDataPoints = _this$props.displayDataPoints,
      legendIndexFormatter = _this$props.legendIndexFormatter,
      yAxisTickCount = _this$props.yAxisTickCount,
      yAxisFormatter = _this$props.yAxisFormatter,
      yHideOrigin = _this$props.yHideOrigin,
      _this$props$axesColor = _this$props.axesColor,
      axesColor = _this$props$axesColor === void 0 ? this.props.theme.constellation.colors.black : _this$props$axesColor,
      tickTextStroke = _this$props.tickTextStroke,
      xAxisFormatter = _this$props.xAxisFormatter,
      xHideOrigin = _this$props.xHideOrigin,
      theme = _this$props.theme,
      xLabel = _this$props.xLabel,
      yLabel = _this$props.yLabel,
      fontFamily = _this$props.fontFamily,
      interactionEventCmsInfo = _this$props.interactionEventCmsInfo;
    var values = data && data[0] && data[0].values;

    // Calculate inner chart dimensions
    var chartWidth, chartHeight;
    chartWidth = containerWidth - margins.left - margins.right;
    chartHeight = height - margins.top - margins.bottom;
    if (legend) {
      chartWidth = chartWidth - legendOffset;
    }
    var dataArray = data;
    if (!Array.isArray(data)) {
      dataArray = [data];
    }
    var flattenedData = flattenData(dataArray, xAccessor, yAccessor);
    var xValues = flattenedData.xValues,
      yValues = flattenedData.yValues;
    var scales = calculateScales(chartWidth, chartHeight, xValues, yValues);
    var mouseOverLine = null;
    var xDomain = scales.xScale.range(),
      yDomain = scales.yScale.range();
    var mouseMoveIsInDomain = true;
    if (mouseX - 20 < xDomain[0] || mouseX - 20 > xDomain[1] || mouseY - 10 < yDomain[1] || mouseY - 10 > yDomain[0]) {
      // Ignore mouse if movement is outside chart domain
      mouseMoveIsInDomain = false;
    }
    var hoverIndex = null;
    if (mouseMoveIsInDomain) {
      var fnLine = line().x(function (d) {
        return d[0];
      }).y(function (d) {
        return d[1];
      });
      hoverIndex = this._nearest(dataArray[0].values, scales.xScale.invert(mouseX - 20), xAccessor);
      mouseOverLine = /*#__PURE__*/React.createElement(Line, {
        path: fnLine([[mouseX - 20, scales.yScale.range()[0]], [mouseX - 20, scales.yScale.range()[1]]]),
        strokeWidth: null,
        stroke: theme.constellation.colors.borderDark
      });
      if (interactionEventCmsInfo) {
        handleFieldChangeEvent(interactionEventCmsInfo.label, interactionEventCmsInfo.block_type);
      }
    }

    // Info Points
    var infoPoints = filter(values, function (v) {
      return v.info;
    });
    var activeInfoPoint = null;
    infoPoints.forEach(function (p) {
      p.active = false;
    });
    if (hoverIndex && infoPoints && !_isEmpty(infoPoints)) {
      activeInfoPoint = minBy(infoPoints, function (point) {
        return Math.abs(point.x - hoverIndex);
      });
      activeInfoPoint.active = true;
    }
    var DataSeries$1 = DataSeries;
    var trans = "translate(" + margins.left + ", " + margins.top + ")";
    var dataSeriesArray = dataArray.map(function (series, idx) {
      return /*#__PURE__*/React.createElement(DataSeries$1, {
        xScale: scales.xScale,
        yScale: scales.yScale,
        mouseX: _this2.mouseX - 20,
        hoverIndex: hoverIndex,
        seriesName: series.name,
        data: series.values,
        width: chartWidth,
        height: chartHeight,
        fill: colors(idx) || theme.constellation.colors.black,
        strokeWidth: lineWidth,
        pointRadius: pointRadius,
        key: series.name,
        xAccessor: series.xAccessor || xAccessor,
        yAccessor: series.yAccessor || yAccessor,
        interpolationType: interpolationType,
        displayDataPoints: displayDataPoints
      }, /*#__PURE__*/React.createElement(ChartInfoPoints, {
        infoPoints: infoPoints,
        xScale: scales.xScale,
        yScale: scales.yScale
      }));
    });
    var name = (_dataArray$ = dataArray[0]) === null || _dataArray$ === void 0 ? void 0 : _dataArray$.name;
    var yFormatter = (_dataArray$2 = dataArray[0]) === null || _dataArray$2 === void 0 ? void 0 : _dataArray$2.formatter;
    var allDataPoints = (_dataArray$0$values = (_dataArray$3 = dataArray[0]) === null || _dataArray$3 === void 0 ? void 0 : _dataArray$3.values) !== null && _dataArray$0$values !== void 0 ? _dataArray$0$values : [];
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Legend, {
      data: dataArray,
      hoverIndex: hoverIndex,
      indexFormatter: legendIndexFormatter,
      yAccessor: yAccessor,
      colors: colors,
      fontFamily: fontFamily
    }), /*#__PURE__*/React.createElement(ChartInfoPointsLegend, {
      activeInfoPoint: activeInfoPoint,
      hoverIndex: hoverIndex
    }), /*#__PURE__*/React.createElement(ChartDescription, {
      name: name,
      xLabel: xLabel !== null && xLabel !== void 0 ? xLabel : '',
      yLabel: yLabel !== null && yLabel !== void 0 ? yLabel : name,
      xFormatter: legendIndexFormatter,
      yFormatter: yFormatter,
      dataPoints: allDataPoints
    }), /*#__PURE__*/React.createElement(Chart, {
      fontFamily: fontFamily,
      legend: legend,
      data: dataArray,
      margins: margins,
      colors: colors,
      width: containerWidth,
      height: height,
      title: title
    }, /*#__PURE__*/React.createElement("g", {
      transform: trans,
      className: "rd3-linechart"
    }, /*#__PURE__*/React.createElement(YAxis, {
      yAxisClassName: "rd3-linechart-yaxis",
      yScale: scales.yScale,
      yAxisTickCount: yAxisTickCount,
      yHideOrigin: yHideOrigin,
      tickStroke: axesColor || theme.constellation.colors.black,
      tickTextStroke: tickTextStroke,
      tickSize: -chartWidth,
      yOrient: "right",
      tickFormatting: yAxisFormatter,
      margins: margins,
      width: chartWidth,
      height: chartHeight,
      stroke: axesColor || theme.constellation.colors.black,
      strokeWidth: "0px"
    }), /*#__PURE__*/React.createElement(XAxis, {
      xAxisClassName: "rd3-linechart-xaxis",
      xScale: scales.xScale,
      xAxisTickCount: xAxisTickCount,
      tickFormatting: xAxisFormatter,
      tickSize: 0,
      xHideOrigin: xHideOrigin,
      xOrient: "bottom",
      margins: margins,
      width: chartWidth,
      height: chartHeight,
      stroke: axesColor,
      tickStroke: axesColor,
      tickTextStroke: tickTextStroke,
      strokeWidth: "2px"
    }), mouseOverLine, dataSeriesArray)));
  };
  return LineChart;
}(Component), _class.propTypes = {
  axesColor: PropTypes.string,
  axesStrokeWidth: PropTypes.number,
  colors: PropTypes.func,
  displayDataPoints: PropTypes.bool,
  height: PropTypes.number,
  legend: PropTypes.bool,
  legendOffset: PropTypes.number,
  margins: PropTypes.object,
  mouseX: PropTypes.number,
  mouseY: PropTypes.number,
  pointRadius: PropTypes.number,
  title: PropTypes.string,
  width: PropTypes.number,
  xAccessor: PropTypes.func,
  xHideOrigin: PropTypes.bool,
  yAccessor: PropTypes.func,
  yHideOrigin: PropTypes.bool,
  containerWidth: PropTypes.number,
  interpolate: PropTypes.bool,
  data: PropTypes.array,
  interpolationType: PropTypes.string,
  lineWidth: PropTypes.number,
  xAxisTickCount: PropTypes.number,
  legendIndexFormatter: PropTypes.func,
  xAxisFormatter: PropTypes.func,
  tickTextStroke: PropTypes.string,
  yAxisFormatter: PropTypes.func,
  yAxisTickCount: PropTypes.number,
  theme: PropTypes.object,
  xLabel: PropTypes.string,
  yLabel: PropTypes.string,
  fontFamily: PropTypes.string,
  interactionEventCmsInfo: PropTypes.object
}, _class.defaultProps = {
  colors: scaleOrdinal(schemeCategory20c),
  displayDataPoints: true,
  height: 200,
  interpolate: false,
  legendOffset: 120,
  margins: {
    top: 10,
    right: 20,
    bottom: 40,
    left: 30
  },
  pointRadius: 3,
  title: '',
  width: 400,
  xAccessor: function xAccessor(d) {
    return d.x;
  },
  yAccessor: function yAccessor(d) {
    return d.y;
  }
}, _class));
var LineChart$1 = MousePosition()(LineChart);

export { LineChart$1 as default };
